import { useState, useEffect, forwardRef  } from "react";
import {
  Card,
  Table,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Button,
  Dropdown,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { formataDataHoraExibicao, getCurrentUser } from "../../common/utils";
import { FaSearch, FaEraser, FaEllipsisV } from "react-icons/fa";
import moment from "moment-timezone";
import StatusBadge from "./StatusBadge";
import Paginacao from "../../common/pagination";

const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <Button
    variant="link"
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="text-dark p-0" 
    style={{ textDecoration: 'none' }}
  >
    <FaEllipsisV size={20} />
    {children}
  </Button>
));

CustomToggle.displayName = 'CustomToggle';

const NavegadoraPage = () => {
  const [reportes, setReportes] = useState({
    results: [],
    count: 0,
    next: null,
    previous: null,
  });
  const [page_numb, setPageNumb] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const initialDataInicio = moment().startOf("month").format("YYYY-MM-DD");
  const initialDataFim = moment().endOf("month").format("YYYY-MM-DD");

  const [filtroDataInicio, setFiltroDataInicio] = useState(initialDataInicio);
  const [filtroDataFim, setFiltroDataFim] = useState(initialDataFim);

  useEffect(() => {
    const consultaReportes = async () => {
      setLoading(true);
      setError("");

      const dados_usuario = getCurrentUser();
      const ambiente_programa_id = dados_usuario?.ambiente_programa_id;

      if (!ambiente_programa_id) {
        setError(
          "Nenhum programa selecionado no ambiente. Não é possível carregar os reportes."
        );
        setLoading(false);
        setReportes({ results: [], count: 0 });
        return;
      }

      try {
        let filtros = `?data_inicio=${filtroDataInicio}&data_fim=${filtroDataFim}&page=${page_numb}&programa_id=${ambiente_programa_id}`;
        const response = await api.get(`/formularios/respostas/${filtros}`);
        setReportes(response.data);
      } catch (error) {
        console.error("Erro ao buscar reportes:", error);
        setError("Ocorreu um erro ao buscar os reportes. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    consultaReportes();
  }, [filtroDataInicio, filtroDataFim, page_numb]);

  const handleFiltrar = (e) => {
    e.preventDefault();
    setPageNumb(1);
  };

  const limparFiltros = () => {
    setFiltroDataInicio(initialDataInicio);
    setFiltroDataFim(initialDataFim);
    setPageNumb(1);
  };

  const onClickPagina = (num_page) => {
    setPageNumb(num_page);
  };

  const handleReportarEventoAdverso = (reporte) => {
    const atendimentoUuid = reporte.dados_atendimento?.uuid;
    const pacienteId = reporte.dados_beneficiario?.id;
    const reporteId = reporte.id;
    const ano = new Date().getFullYear();

    if (!atendimentoUuid || !pacienteId) {
      setError(
        "Não foi possível iniciar o reporte. Faltam dados de atendimento ou paciente."
      );
      return;
    }

    // Regra solicitada pela Sawana da Aditus (número ID paciente + número do relato + ano)
    const reportRef = `${pacienteId}-${reporteId}-${ano}`;
    
    /*const navigationState = {
      state: {
        openPTC: true,
        reportRef: reportRef,
      },
    };*/

    const url = `/atendimentos/edit/${atendimentoUuid}?openPTC=true&reportRef=${reportRef}`;

    window.open(url, "_blank");

    //navigate(`/atendimentos/edit/${atendimentoUuid}`, navigationState);
  };

  const renderActionButton = (reporte) => {
    let acoes = [];

    switch (reporte.status) {
      case "enviado":
        acoes.push(
          <Dropdown.Item
            key="visualizar"
            onClick={() => navigate(`/reportes-farmaco/detalhe/${reporte.id}`)}
          >
            Visualizar
          </Dropdown.Item>
        );
        break;
      case "em_analise":
        acoes.push(
          <Dropdown.Item
            key="continuar"
            onClick={() =>
              navigate(
                `/reportes-farmaco/editar/${reporte.copia_de_trabalho_id}`
              )
            }
          >
            Continuar Análise
          </Dropdown.Item>,
          <Dropdown.Item
            key="ver_original"
            onClick={() =>
              navigate(`/reportes-farmaco/detalhe/${reporte.id}`)
            }
          >
            Ver Original
          </Dropdown.Item>
        );
        break;
      case "analise_finalizada":
        acoes.push(
          <Dropdown.Item
            key="ver_final"
            onClick={() =>
              navigate(
                `/reportes-farmaco/detalhe/${reporte.copia_de_trabalho_id}`
              )
            }
          >
            Ver Versão Final
          </Dropdown.Item>
        );
        break;
      default:
        break;
    }

    if (reporte.dados_atendimento?.id) {
      if (acoes.length > 0) {
        acoes.push(<Dropdown.Divider key="divider" />);
      }
      
      acoes.push(
        <Dropdown.Item
          key="reportar_ea"
          onClick={() => handleReportarEventoAdverso(reporte)}
          className="text-primary"
        >
          Reportar Evento Adverso
        </Dropdown.Item>
      );
    }

    if (acoes.length === 0) {
      return null;
    }

    return (
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${reporte.id}`} />
        <Dropdown.Menu align="end">{acoes}</Dropdown.Menu>
      </Dropdown>
    );
  };


  return (
    <div>
      <Row className="align-items-center">
        <Col sm={6} lg={6}>
          <h4 className="page-title">
            Reportes de Farmacovigilância Recebidos
          </h4>
        </Col>
      </Row>
      <Card className="cardDestaque">
        <Card.Body>
          <Form onSubmit={handleFiltrar} className="mb-4">
            <h4>Filtros</h4>
            <Row>
              <Col sm={3}>
                <Form.Group>
                  <Form.Label>Data Início</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtroDataInicio}
                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group>
                  <Form.Label>Data Fim</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtroDataFim}
                    onChange={(e) => setFiltroDataFim(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              className="mt-3"
            >
              <FaSearch /> Filtrar
            </Button>
            <Button
              type="button"
              variant="light"
              size="sm"
              className="mt-3 ms-2"
              onClick={limparFiltros}
            >
              <FaEraser /> Limpar
            </Button>
          </Form>
        </Card.Body>
      </Card>
      <Card className="cardDestaque">
        <Card.Body className="pt-0">
          {error && <Alert variant="danger">{error}</Alert>}
          <Table responsive className="custom-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Data de Envio</th>
                <th>Prestador</th>
                <th>Programa</th>
                <th>Paciente</th>
                <th>Atendimento Vinculado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : reportes.results && reportes.results.length > 0 ? (
                reportes.results.map((reporte) => (
                  <tr key={reporte.id}>
                    <td>{reporte.id}</td>
                    <td>
                      <StatusBadge status={reporte.status} />
                    </td>
                    <td>{formataDataHoraExibicao(reporte.data_cadastro)}</td>
                    <td>{reporte.dados_prestador?.nome || "-"}</td>
                    <td>{reporte.dados_programa?.nome || "-"}</td>
                    <td>
                      {reporte.dados_beneficiario?.nome ||
                        reporte.respostas?.paciente_nome_iniciais ||
                        "-"}
                    </td>
                    <td>{reporte.dados_atendimento?.id || "-"}</td>
                    <td>{renderActionButton(reporte)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    Nenhum reporte encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {reportes.results && reportes.results.length > 0 && (
            <Paginacao
              dados={reportes}
              loading={loading}
              page_numb={page_numb}
              _onClickPagina={onClickPagina}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NavegadoraPage;
